import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { motion } from 'framer-motion';
import {
  X,
  Download,
  Copy,
  Check,
  Share2,
  Facebook,
  Twitter,
  MessageCircle,
  QrCode,
  Smartphone,
  Link as LinkIcon,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

// Upload the image to a public URL so the QR code is scannable from any phone.
// Tries multiple temporary hosts (1h–12h retention) — perfect for event photobooths.
async function uploadToPublicHost(imageUrl) {
  // Convert any source (blob/data/http) into a Blob we can FormData-upload
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const file = new File([blob], 'photobooth.jpg', { type: 'image/jpeg' });

  const tryFetch = async (url, opts, parser) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch(url, { ...opts, signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await parser(res);
    } catch (e) { clearTimeout(timer); throw e; }
  };

  // 1. tmpfiles.org — 1 hour retention
  try {
    const form = new FormData();
    form.append('file', file);
    const url = await tryFetch('https://tmpfiles.org/api/v1/upload', { method: 'POST', body: form }, async (r) => {
      const j = await r.json();
      if (j.status === 'success') {
        return j.data.url.replace('tmpfiles.org/', 'tmpfiles.org/dl/').replace('http://', 'https://');
      }
      throw new Error('tmpfiles bad response');
    });
    console.log('📤 Uploaded for QR sharing →', url);
    return url;
  } catch (e) { console.warn('tmpfiles failed:', e.message); }

  // 2. Catbox litterbox — 12 hours
  try {
    const form = new FormData();
    form.append('reqtype', 'fileupload');
    form.append('time', '12h');
    form.append('fileToUpload', file, 'photobooth.jpg');
    const url = await tryFetch('https://litterbox.catbox.moe/resources/internals/api.php', { method: 'POST', body: form }, async (r) => {
      const txt = (await r.text()).trim();
      if (txt.startsWith('https')) return txt;
      throw new Error('litterbox bad response');
    });
    console.log('📤 Uploaded for QR sharing →', url);
    return url;
  } catch (e) { console.warn('litterbox failed:', e.message); }

  throw new Error('All upload hosts failed');
}

export default function ShareModal({ isOpen, onClose, imageUrl, characterName }) {
  const { language, isRTL } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [publicImageUrl, setPublicImageUrl] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle | uploading | success | error
  const qrRef = useRef(null);

  // Decide how to surface the image to the QR target:
  //   • If the URL is already publicly addressable (FAL CDN, our /api/uploads, http(s)) → use it as-is
  //   • Otherwise (blob: / data:) → upload to a temporary public host
  useEffect(() => {
    if (!isOpen || !imageUrl) return;

    const isPublic = typeof imageUrl === 'string' && (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://') ||
      imageUrl.startsWith('/api/uploads/')
    );

    if (isPublic) {
      // Resolve relative /api/uploads/... to full URL for the QR code
      const fullUrl = imageUrl.startsWith('/')
        ? `${window.location.origin}${imageUrl}`
        : imageUrl;
      setPublicImageUrl(fullUrl);
      setUploadStatus('success');
      return;
    }

    // blob: / data: — must upload so it works cross-device
    setPublicImageUrl(null);
    setUploadStatus('uploading');
    uploadToPublicHost(imageUrl)
      .then((url) => { setPublicImageUrl(url); setUploadStatus('success'); })
      .catch((e) => { console.error('Public upload failed:', e); setUploadStatus('error'); });
  }, [isOpen, imageUrl]);

  // QR code encodes the public URL (when available)
  const qrTarget = publicImageUrl || imageUrl;
  const qrCodeUrl = qrTarget
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrTarget)}&bgcolor=1e1b4b&color=a78bfa`
    : null;

  const shareText = language === 'he'
    ? `ראו מה יצרתי עם AI PhotoBooth! הפכתי את עצמי ל${characterName}`
    : `Check out what I created with AI PhotoBooth! I transformed into ${characterName}`;

  const handleCopyLink = async () => {
    if (!publicImageUrl) {
      toast.error(language === 'he' ? 'הקישור עדיין מועלה...' : 'Link still uploading...');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicImageUrl);
      setCopied(true);
      toast.success(language === 'he' ? 'הקישור הועתק!' : 'Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qr-${characterName || 'photobooth'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async (platform) => {
    const linkToShare = publicImageUrl || imageUrl;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(linkToShare)}&quote=${encodeURIComponent(shareText)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(linkToShare)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + linkToShare)}`,
    };

    if (platform === 'native' && navigator.share) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'ai-photobooth.jpg', { type: 'image/jpeg' });

        await navigator.share({
          title: 'AI PhotoBooth',
          text: shareText,
          files: [file],
          url: linkToShare
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-xl flex items-center gap-3">
            <Share2 className="w-6 h-6 text-violet-400" />
            {language === 'he' ? 'שתפו את היצירה שלכם' : 'Share Your Creation'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* QR Code Section */}
          <div className="text-center">
            <div className="inline-block p-4 bg-slate-800 rounded-2xl mb-4 relative">
              {uploadStatus === 'uploading' && (
                <div className="w-60 h-60 flex flex-col items-center justify-center gap-3 text-slate-400">
                  <Loader2 className="w-10 h-10 animate-spin text-violet-400" />
                  <p className="text-sm">
                    {language === 'he' ? 'מעלה את התמונה לשרת...' : 'Uploading image...'}
                  </p>
                </div>
              )}
              {uploadStatus === 'success' && qrCodeUrl && (
                <img
                  ref={qrRef}
                  src={qrCodeUrl}
                  alt="QR Code"
                  className="w-60 h-60 rounded-lg"
                />
              )}
              {uploadStatus === 'error' && (
                <div className="w-60 h-60 flex flex-col items-center justify-center gap-3 text-red-400 text-sm p-4 text-center">
                  <X className="w-10 h-10" />
                  <p>
                    {language === 'he'
                      ? 'לא ניתן ליצור קישור לשיתוף. נסו להוריד ולשלוח ידנית.'
                      : 'Could not create share link. Try downloading and sending manually.'}
                  </p>
                </div>
              )}
            </div>
            <p className="text-slate-400 text-sm mb-3">
              <Smartphone className="w-4 h-4 inline me-2" />
              {language === 'he'
                ? 'סרקו עם הטלפון להורדה מהירה'
                : 'Scan with your phone for quick download'}
            </p>
            {uploadStatus === 'success' && (
              <Button
                onClick={handleDownloadQR}
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <QrCode className="w-4 h-4 me-2" />
                {language === 'he' ? 'הורד QR' : 'Download QR'}
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-slate-900 px-4 text-sm text-slate-500">
                {language === 'he' ? 'או שתפו ברשתות' : 'or share on social'}
              </span>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div className="grid grid-cols-4 gap-3">
            {navigator.share && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleShare('native')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-violet-600/20 to-pink-600/20 border border-violet-500/30 hover:border-violet-500/50 transition-colors"
              >
                <Share2 className="w-6 h-6 text-violet-400" />
                <span className="text-xs text-slate-400">
                  {language === 'he' ? 'שתף' : 'Share'}
                </span>
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleShare('whatsapp')}
              disabled={!publicImageUrl}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-green-500/10 border border-green-500/30 hover:border-green-500/50 transition-colors disabled:opacity-40"
            >
              <MessageCircle className="w-6 h-6 text-green-400" />
              <span className="text-xs text-slate-400">WhatsApp</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleShare('facebook')}
              disabled={!publicImageUrl}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 hover:border-blue-500/50 transition-colors disabled:opacity-40"
            >
              <Facebook className="w-6 h-6 text-blue-400" />
              <span className="text-xs text-slate-400">Facebook</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleShare('twitter')}
              disabled={!publicImageUrl}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-sky-500/10 border border-sky-500/30 hover:border-sky-500/50 transition-colors disabled:opacity-40"
            >
              <Twitter className="w-6 h-6 text-sky-400" />
              <span className="text-xs text-slate-400">Twitter</span>
            </motion.button>
          </div>

          {/* Copy Link */}
          <div className="flex gap-2">
            <div className="flex-1 bg-slate-800/50 rounded-lg px-4 py-3 text-slate-400 text-sm truncate">
              {publicImageUrl || (uploadStatus === 'uploading'
                ? (language === 'he' ? 'מעלה...' : 'Uploading...')
                : (language === 'he' ? 'אין קישור זמין' : 'No link available'))}
            </div>
            <Button
              onClick={handleCopyLink}
              variant="outline"
              disabled={!publicImageUrl}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 shrink-0 disabled:opacity-40"
            >
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>

          {/* Info Text */}
          <div className="text-center p-3 rounded-xl bg-slate-800/40 border border-slate-700/40">
            <p className="text-xs text-slate-500">
              {language === 'he'
                ? '⏱️ הקישור פעיל לשעה אחת — מספיק זמן לכל האורחים לסרוק ולהוריד'
                : '⏱️ Link is valid for 1 hour — enough time for all guests to scan and download'}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
